// Maps GET /quotes list items and GET /quotes/:id onto the receptor
// inbox/detail view. Missing additive fields stay empty; the client does not
// fill them from the inbox cache.

import { formatCLP } from '@/lib/format.js';
import {
  actuationLabel,
  addressTypeLabel,
  conservadorFields,
  recipientLines,
} from '@/lib/taskActuation.js';
import { breakdownRows, offerBreakdownLines } from '@/lib/priceBreakdown.js';

function text(value) {
  if (value == null) return '';
  return String(value).trim();
}

export function quoteAddress(quote) {
  return [quote?.taskAddress, quote?.taskAddressDetails]
    .map(text)
    .filter(Boolean)
    .join(', ');
}

export function quoteParty(quote) {
  const firm = text(quote?.firmName);
  const title = text(quote?.caseTitle);
  if (firm && title) return `${firm} / ${title}`;
  return firm || title || '—';
}

export function quoteTipo(quote) {
  return text(quote?.taskNotes) || text(quote?.notes);
}

// What the receptor can still do with the quote, read from the status.
// acceptQuote and rejectQuote refuse OFFERED and DECLINED with
// QuoteAlreadyAnswered, and acceptQuote also refuses a task outside the quoting
// cycle with TaskNotAcceptingOffers. PAID is a legacy name for an accepted
// offer, so it reads as offered. EMAIL_FAILED is an invite whose email bounced
// and takes an offer like INVITED. The API status guard would accept an offer
// on EXPIRED (no longer written) or on an unknown status; keeping both
// read-only is a product choice, not an API rule.
const STATE_BY_STATUS = {
  INVITE_PENDING: 'open',
  INVITED: 'open',
  EMAIL_FAILED: 'open',
  OFFERED: 'offered',
  PAID: 'offered',
  DECLINED: 'declined',
};

// A price only counts when it is a positive amount: a 0 left by an old row
// would read as "Cotizaste $0".
function hasPrice(price) {
  return text(price) !== '' && Number(price) > 0 && formatCLP(price) != null;
}

// DECLINED is not always the receptor's own rejection: when the firm pays
// another receptor, the API declines the sibling quotes and keeps their price
// and dateOffered. A DECLINED quote with a price was an offer the firm did not
// choose (lost); without one the view cannot tell who closed it (declined).
export function quoteState(status, price) {
  const state = STATE_BY_STATUS[status] ?? 'closed';
  if (state === 'declined' && hasPrice(price)) return 'lost';
  return state;
}

export function quotePriceLabel(state, price) {
  if (state === 'offered') return formatCLP(price) ?? 'Cotizada';
  if (state === 'lost') return 'No elegida';
  if (state === 'declined' || state === 'closed') return 'Cerrada';
  return 'Por cotizar';
}

// What an answered quote reads as in the Respondidas inbox. DECLINED without a
// price stays "Cerrada": the view cannot tell the receptor's own rejection from
// an invite the API closed when the firm paid someone else.
export function quoteAnswerLabel(state, paidAt) {
  if (state === 'offered') return paidAt ? 'Pagada' : 'Cotizada';
  if (state === 'lost') return 'No elegida';
  if (state === 'declined' || state === 'closed') return 'Cerrada';
  return null;
}

export function mapQuoteToInboxItem(quote) {
  if (!quote?.id) return null;
  const state = quoteState(quote.status, quote.price);
  return {
    id: quote.id,
    rol: text(quote.caseRol) || '—',
    party: quoteParty(quote),
    // The actuation the firm asked for; a task from before REC-1966 has none
    // and keeps showing its notes there.
    tipo: actuationLabel(quote) || quoteTipo(quote),
    comuna: text(quote.taskCommune),
    state,
    priceLabel: quotePriceLabel(state, quote.price),
    answerLabel: quoteAnswerLabel(state, quote.paidAt),
    offeredPrice: hasPrice(quote.price) ? formatCLP(quote.price) : null,
    taskDeadline: quote.taskDeadline ?? null,
    taskDeadlineExpiresAt: quote.taskDeadlineExpiresAt ?? null,
  };
}

export function mapQuoteToDetail(quote) {
  const item = mapQuoteToInboxItem(quote);
  if (!item) return null;
  return {
    ...item,
    address: quoteAddress(quote),
    region: text(quote.taskRegion),
    tribunalName: text(quote.tribunalName),
    firmName: text(quote.firmName),
    notes: quoteTipo(quote),
    actuacion: actuationLabel(quote),
    addressType: addressTypeLabel(quote.addressType),
    procedure: text(quote.procedure),
    conservador: conservadorFields(quote),
    recipients: recipientLines(quote),
    // One priced row per recipient, when the request names them (REC-1983).
    breakdownRows: breakdownRows(quote.recipients),
    // How the offer already made adds up, per recipient.
    offerBreakdown: offerBreakdownLines(quote.priceBreakdown, quote.recipients),
    // The account this offer is paid to (the receptor's read only): the one
    // it was made with, not today's principal.
    payoutAccount: quote.payoutAccount ?? null,
    status: quote.status ?? null,
    price: quote.price ?? null,
    dateOffered: quote.dateOffered ?? null,
    paidAt: quote.paidAt ?? null,
  };
}
